/**
 * 客服对话框 Widget 组件
 *
 * 设计规范（来自 pencil）：
 * - 尺寸：380×640px
 * - 圆角：16px
 * - 阴影：0 8px 32px rgba(0, 0, 0, 0.12)
 * - 结构：WidgetHeader + ChatArea + QuickTags + InputArea
 *
 * 可访问性：
 * - role="dialog" 标识对话框
 * - aria-label 标识客服对话框
 * - 支持关闭操作
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import { WidgetHeader } from './WidgetHeader';
import { WidgetMessageArea } from './WidgetMessageArea';
import { WidgetInputArea } from './WidgetInputArea';
import { QuickTags } from './QuickTags';
import { HistoryModal } from './HistoryModal';
import { useChatStore } from '../../stores/chatStore';

export interface ChatWidgetProps {
  /** Widget 是否打开 */
  isOpen?: boolean;
  /** 打开状态变化回调 */
  onOpenChange?: (isOpen: boolean) => void;
  /** 关闭回调 */
  onClose?: () => void;
  /** 自定义 CSS 类名 */
  className?: string;
  /** 位置：固定定位或绝对定位 */
  position?: 'fixed' | 'absolute';
  /** 默认选中的标签 */
  defaultTag?: string;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({
  isOpen = true,
  onOpenChange,
  onClose,
  className,
  position = 'fixed',
  defaultTag,
}) => {
  const [selectedTag, setSelectedTag] = useState<string | undefined>(defaultTag);

  // chatStore 状态和方法
  const {
    sendMessage,
    isConnected,
    isTyping,
    // 会话管理
    sessions,
    currentSessionId,
    isLoadingSessions,
    // Widget UI 状态
    isHistoryModalOpen,
    // Widget UI 操作
    startNewSession,
    openHistoryModal,
    closeHistoryModal,
    confirmDeleteSession,
    switchSession,
  } = useChatStore();

  // 处理关闭操作
  const handleClose = useCallback(() => {
    onOpenChange?.(false);
    onClose?.();
  }, [onOpenChange, onClose]);

  // 处理新建对话
  const handleNewChat = useCallback(() => {
    startNewSession();
  }, [startNewSession]);

  // 处理打开历史记录
  const handleOpenHistory = useCallback(() => {
    openHistoryModal();
  }, [openHistoryModal]);

  // 处理关闭历史记录
  const handleCloseHistory = useCallback(() => {
    closeHistoryModal();
  }, [closeHistoryModal]);

  // 处理选择会话
  const handleSelectSession = useCallback(async (sessionId: string) => {
    await switchSession(sessionId);
    closeHistoryModal();
  }, [switchSession, closeHistoryModal]);

  // 处理关闭会话请求
  const handleCloseSessionClick = useCallback((sessionId: string) => {
    confirmDeleteSession(sessionId); // 借用现有的 confirm 逻辑，但改为关闭
  }, [confirmDeleteSession]);

  // 处理消息发送
  const handleSendMessage = useCallback(async (content: string) => {
    await sendMessage(content);
  }, [sendMessage]);

  // 处理标签选择
  const handleTagSelect = useCallback((tag: string) => {
    setSelectedTag(tag);
    // 发送标签作为消息
    sendMessage(tag);
  }, [sendMessage]);

  // 处理建议卡片点击
  const handleSuggestionClick = useCallback((label: string) => {
    sendMessage(label);
  }, [sendMessage]);

  // 键盘事件：ESC 关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={clsx(
        'z-50',
        position === 'fixed' && 'fixed bottom-4 right-4',
        position === 'absolute' && 'absolute bottom-4 right-4',
        className
      )}
      style={{
        width: '380px',
        height: '640px',
      }}
    >
      {/* Widget 外壳 */}
      <div
        className={clsx(
          'w-full h-full flex flex-col overflow-hidden',
          'bg-transparent',
          'rounded-[var(--widget-border-radius, 16px)]',
          'shadow-[var(--widget-shadow, 0_8px_32px_rgba(0,0,0,0.12)]'
        )}
        role="dialog"
        aria-label="智能客服对话框"
        aria-modal="true"
      >
        {/* 顶部栏 */}
        <WidgetHeader
          isConnected={isConnected}
          onClose={handleClose}
          onNewChat={handleNewChat}
          onHistory={handleOpenHistory}
        />

        {/* 消息区域 */}
        <WidgetMessageArea
          showSuggestions={false}
          onSuggestionClick={handleSuggestionClick}
        />

        {/* 快捷标签 */}
        <QuickTags
          selectedTag={selectedTag}
          onSelect={handleTagSelect}
        />

        {/* 输入区域 */}
        <WidgetInputArea
          onSend={handleSendMessage}
          disabled={!isConnected || isTyping}
          placeholder="输入您的问题..."
        />
      </div>

      {/* 历史记录模态框 */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        sessions={sessions}
        currentSessionId={currentSessionId}
        isLoading={isLoadingSessions}
        onClose={handleCloseHistory}
        onSelectSession={handleSelectSession}
        onCloseSessionClick={handleCloseSessionClick}
      />

      {/* 移除原有的 ConfirmDialog，因为 HistoryModal 内部已经处理了确认 */}
    </div>
  );
};
